// Los datos de trabajo, en el teléfono.
//
// EL HUECO QUE CIERRA. Las tres colas —movimientos, conteos y
// recepciones— garantizan que el trabajo HECHO sin señal no se pierde. No
// resolvían lo contrario: si el teléfono se recarga sin cobertura, no se
// puede EMPEZAR. La hoja de conteo y la lista de órdenes vienen de la API,
// y sin ella la pantalla queda vacía. El operador tiene el trabajo delante
// —la tarima, el pasillo— y el sistema no le enseña contra qué hacerlo.
//
// Se guarda la última respuesta buena de las rutas de las que se trabaja, y
// se usa cuando la API no contesta.
//
// LA REGLA QUE NO SE PUEDE ROMPER: UN DATO DE CACHÉ SIEMPRE SE ANUNCIA
// COMO TAL, CON SU HORA. Enseñar una lista vieja como si fuera de ahora es
// peor que no enseñar nada — el operador toma decisiones sobre existencias
// que ya cambiaron y ni siquiera sabe que debería dudar. Por eso lo que se
// guarda lleva marca de tiempo y `useDatosDeTrabajo` obliga a devolverla.
//
// AISLAMIENTO. Cada entrada va marcada con el id del usuario que la trajo,
// y solo se devuelve a ese mismo usuario. En un teléfono compartido entre
// turnos —que es lo normal en un almacén— la caché de uno no puede
// aparecerle a otro, y menos si son de empresas distintas. El id de
// usuario es único entre corporaciones, así que sirve para las dos cosas.
// Además se borra entera al cerrar sesión: esto es el cinturón, aquello
// los tirantes.
const DB = 'tego-trabajo';
const STORE = 'respuestas';

// A los siete días se tira. No es por espacio: es que pasada una semana un
// dato viejo engaña más de lo que ayuda, y la etiqueta con la hora deja de
// bastar porque nadie la lee al décimo día.
const VIGENCIA_MS = 7 * 24 * 60 * 60 * 1000;

function abrir() {
  return new Promise((res, rej) => {
    const r = indexedDB.open(DB, 1);
    r.onupgradeneeded = () => {
      if (!r.result.objectStoreNames.contains(STORE)) {
        r.result.createObjectStore(STORE, { keyPath: 'clave' });
      }
    };
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });
}

function conStore(modo, fn) {
  return abrir().then((d) => new Promise((res, rej) => {
    const t = d.transaction(STORE, modo);
    const rq = fn(t.objectStore(STORE));
    t.oncomplete = () => res(rq && rq.result);
    t.onerror = () => rej(t.error);
    t.onabort = () => rej(t.error);
  }));
}

const clave = (idUsuario, ruta) => `${idUsuario}:${ruta}`;

/** Guarda la última respuesta buena de `ruta`. Nunca lanza. */
export async function guardarTrabajo(idUsuario, ruta, datos) {
  if (!idUsuario) return;
  try {
    await conStore('readwrite', (s) => s.put({
      clave: clave(idUsuario, ruta),
      id_usuario: idUsuario,
      ruta,
      datos,
      _ts: Date.now(),
    }));
  } catch {
    // Que no quepa la copia no puede tumbar la pantalla que sí cargó.
  }
}

/**
 * Lo guardado para `ruta`, o null. Devuelve `{datos, momento}`.
 *
 * `momento` es obligatorio en la respuesta a propósito: quien lee de aquí
 * no puede pintar el dato sin tener a mano cuándo se trajo.
 */
export async function leerTrabajo(idUsuario, ruta) {
  if (!idUsuario) return null;
  try {
    const e = await conStore('readonly', (s) => s.get(clave(idUsuario, ruta)));
    if (!e) return null;
    // La comprobación se repite aunque la clave ya lleve el usuario: si
    // algún día la clave cambia de forma, esto sigue siendo cierto.
    if (e.id_usuario !== idUsuario) return null;
    if (Date.now() - e._ts > VIGENCIA_MS) {
      await conStore('readwrite', (s) => s.delete(e.clave));
      return null;
    }
    return { datos: e.datos, momento: new Date(e._ts) };
  } catch {
    return null;
  }
}

/**
 * ¿Este fallo justifica enseñar la copia?
 *
 * LA COPIA SOLO TAPA LO QUE ES FALTA DE RED. Un error sin `status` es que
 * no se llegó al servidor; un 5xx es que el servidor se cayó. Todo lo
 * demás —401 de sesión caducada, 403 de permiso retirado, 404 de algo que
 * ya no existe— es una respuesta CIERTA, y taparla con la copia dejaría al
 * operador trabajando contra una pantalla que ya no le corresponde en vez
 * de mandarlo a iniciar sesión. Una caché que se traga los 401 sirve datos
 * viejos para siempre.
 *
 * Tiene nombre propio, y no vive dentro del hook, porque es la regla más
 * fácil de romper sin darse cuenta —basta con «simplificar» el catch— y
 * porque así se puede probar sin montar media aplicación.
 */
export function sirveLaCopia(error) {
  return !error?.status || error.status >= 500;
}

/** Se llama al cerrar sesión: en el teléfono no queda nada del turno. */
export async function limpiarTrabajo() {
  try {
    await conStore('readwrite', (s) => s.clear());
  } catch { /* si no se puede, la vigencia acaba tirándolo igual */ }
}
